import type { LegendLabel } from '@better-lights/core';
import { DEFAULT_LINE_COLOR, normalizeLegendKeys } from '@better-lights/core';
import { useAccount } from '../account/store.ts';
import { uuidv7 } from '../lib/uuid.ts';
import { blobUri } from '../storage/blobStore.ts';
import { db } from '../storage/db.ts';
import { storage } from '../storage/index.ts';
import { type ProjectFile, SCHEMA_VERSION } from '../storage/record.ts';
import type { AppSettings } from '../storage/settings.ts';
import { syncHooks } from '../sync/hooks.ts';
import { normalizePhoto } from './import.ts';

/** Org workspaces start new projects from the shared price list (copied, so later edits never change old quotes). */
async function catalogLabels(): Promise<Record<string, LegendLabel> | null> {
  const { me, activeScope } = useAccount.getState();
  if (!me || !activeScope || activeScope === me.personalScope) return null;
  const cat = (await db().kv.get(`catalog.${activeScope}`))?.value as
    | { doc?: { legendLabels?: Record<string, LegendLabel> } }
    | undefined;
  return cat?.doc?.legendLabels ? normalizeLegendKeys(cat.doc.legendLabels) : null;
}

/** Imports a picked / captured photo and saves a new project for it (UUIDv7 id). */
export async function createProjectFromPhoto(
  file: Blob,
  settings: AppSettings,
  now = Date.now(),
): Promise<ProjectFile> {
  const n = await normalizePhoto(file);
  const { blobs, projects } = storage();
  const photoSha = await blobs.put(n.photo);
  const thumbSha = await blobs.put(n.thumb);
  const project: ProjectFile = {
    schemaVersion: SCHEMA_VERSION,
    id: uuidv7(now),
    createdAt: now,
    updatedAt: now,
    photo: {
      uri: blobUri(photoSha),
      width: n.width,
      height: n.height,
      sha256: photoSha,
      bytes: n.photo.size,
      mime: 'image/jpeg',
    },
    thumb: blobUri(thumbSha),
    doc: {
      runs: [],
      time: 1,
      colorId: settings.defaultColorId,
      style: { ...settings.defaultStyle },
      frameWidthFt: null,
      drawLineColor: DEFAULT_LINE_COLOR,
      legendLabels: (await catalogLabels()) ?? { ...settings.legendLabels },
      view: 'demo',
    },
    aiResults: [],
    exports: [],
  };
  await projects.save(project);
  syncHooks.projectCreated(project.id);
  return project;
}
